import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { HardDrive, Cloud, RotateCcw, Trash2, CloudUpload, Download, History, Check } from 'lucide-react';
import { backupStore } from '../../../stores/backup.store';
import { cn } from '@/utils/cn';
import { dayLabel, timeLabel, kindLabel, prettySize, backupKind } from '../../../utils/backup-format';
import type { StoredBackup } from '../../../types';
import type { TimelineItem } from './types';

const PAGE = 25;

const KIND_TONE: Record<string, string> = {
  daily: 'text-ember bg-ember/10 border-ember/25',
  hourly: 'text-online bg-online/10 border-online/25',
  onstop: 'text-warn bg-warn/10 border-warn/25',
  manual: 'text-fg bg-white/[0.06] border-line',
  autoupdate: 'text-[#B59CFF] bg-[#B59CFF]/10 border-[#B59CFF]/25',
  auto: 'text-fg-muted bg-white/[0.04] border-line',
};

const asStored = (b: TimelineItem): StoredBackup => ({ id: b.id, name: b.name, driveId: b.driveId, date: b.date, size: b.size, type: b.type });

const Row = observer(({ item, restoreDisabled }: { item: TimelineItem; restoreDisabled: boolean }) => {
  const selected = backupStore.selectedBackups.has(item.key);
  const kind = backupKind(item.type);

  const restore = () => {
    if (item.isLocal && item.path) backupStore.handleRestoreLocalBackup({ path: item.path, name: item.name });
    else if (item.driveId) backupStore.handleRestoreBackup(asStored(item));
  };
  const transfer = () => {
    if (item.isLocal) backupStore.transferBackup({ name: item.name, path: item.path }, 'to-cloud');
    else backupStore.transferBackup({ driveId: item.driveId, name: item.name, size: item.size, type: item.type }, 'to-local');
  };
  const remove = () => (item.isLocal ? backupStore.deleteLocalBackup(item.name) : backupStore.handleDeleteBackup(asStored(item)));

  return (
    <div className={cn(
      'group flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 px-4 py-2.5 rounded-xl transition-colors',
      selected ? 'bg-ember/[0.07]' : 'hover:bg-white/[0.03]',
    )}>
      <button
        onClick={() => backupStore.toggleBackupSelection(item.key)}
        aria-label={selected ? 'Auswahl aufheben' : 'Auswählen'}
        aria-pressed={selected}
        className={cn(
          'w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 transition-colors',
          selected ? 'bg-ember border-ember text-ink' : 'border-line-strong text-transparent hover:border-fg-muted',
        )}
      >
        <Check size={12} strokeWidth={3} />
      </button>
      <span className="font-mono text-sm text-fg w-12 shrink-0">{timeLabel(item.ms)}</span>
      <span className={cn('inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md border shrink-0', item.isLocal ? 'text-fg border-line bg-white/[0.04]' : 'text-sky-300 border-sky-400/25 bg-sky-400/10')}>
        {item.isLocal ? <HardDrive size={12} /> : <Cloud size={12} />}
        {item.isLocal ? 'HDD' : 'Cloud'}
      </span>
      <span className={cn('text-xs px-2 py-0.5 rounded-md border shrink-0', KIND_TONE[kind])}>{kindLabel(item.type)}</span>
      <span className="text-xs text-fg-muted font-mono flex-1 min-w-[70px]">{prettySize(item.size)}</span>

      <div className="flex items-center gap-1 ml-auto">
        <button
          onClick={transfer}
          title={item.isLocal ? 'Kopie in die Cloud hochladen' : 'Kopie auf die HDD holen'}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-fg-muted hover:text-fg hover:bg-white/[0.06] transition-colors"
        >
          {item.isLocal ? <CloudUpload size={15} /> : <Download size={15} />}
        </button>
        <button
          onClick={remove}
          title="Backup löschen"
          className="w-8 h-8 rounded-lg flex items-center justify-center text-fg-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <Trash2 size={15} />
        </button>
        <button
          onClick={restore}
          disabled={restoreDisabled}
          className="ml-1 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium border border-line-strong text-fg hover:border-ember/60 hover:text-ember transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <RotateCcw size={13} /> Wiederherstellen
        </button>
      </div>
    </div>
  );
});

/** Alle Wiederherstellungspunkte als Zeitleiste – nach Tagen gruppiert, neueste zuerst. */
export const BackupTimeline = observer(({ items, restoreDisabled, loading }: { items: TimelineItem[]; restoreDisabled: boolean; loading: boolean }) => {
  const [limit, setLimit] = useState(PAGE);
  const shown = items.slice(0, limit);
  const groups: [string, TimelineItem[]][] = [];
  for (const it of shown) {
    const label = dayLabel(it.ms);
    const g = groups[groups.length - 1];
    if (g && g[0] === label) g[1].push(it);
    else groups.push([label, [it]]);
  }

  return (
    <div className="bg-surface border border-line rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-line">
        <div className="flex items-center gap-2 text-sm font-medium text-fg">
          <History size={15} className="text-fg-muted" /> Wiederherstellungspunkte
        </div>
        <span className="text-xs text-fg-muted">Ein Klick auf „Wiederherstellen" setzt den Server auf diesen Stand zurück.</span>
      </div>

      {items.length === 0 ? (
        <div className="py-12 text-center text-sm text-fg-muted">
          {loading ? 'Lade Backups …' : 'Noch keine Backups – oben rechts „Jetzt sichern" oder unten einen Zeitplan einschalten.'}
        </div>
      ) : (
        <div className="p-2">
          {groups.map(([label, list]) => (
            <div key={label} className="mb-1">
              <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted">{label}</div>
              {list.map((it) => <Row key={it.key} item={it} restoreDisabled={restoreDisabled} />)}
            </div>
          ))}
          {items.length > limit && (
            <button onClick={() => setLimit((l) => l + PAGE)} className="w-full py-2.5 text-xs text-fg-muted hover:text-fg transition-colors">
              Ältere anzeigen ({items.length - limit} weitere)
            </button>
          )}
        </div>
      )}
    </div>
  );
});
